import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toast';
import {
  resendVerificationEmail,
  RESEND_COOLDOWN_SECONDS,
} from '../../utils/verificationEmailUtils';

const getLinkLabel = ({ isResending, cooldown }) => {
  if (isResending) return 'Sending...';
  if (cooldown > 0) return `Resend in ${cooldown}s`;
  return 'Resend verification email';
};

const ResendVerificationEmail = ({
  defaultEmail = '',
  showEmailInput = true,
  readOnlyEmail = false,
  variant = 'compact',
  className = '',
  onSuccess,
}) => {
  const [email, setEmail] = useState(defaultEmail);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    setEmail(defaultEmail);
  }, [defaultEmail]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const validateEmail = useCallback(() => {
    if (!email.trim()) {
      return 'Email is required';
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      return 'Email address is invalid';
    }
    return '';
  }, [email]);

  const handleResend = async () => {
    const validationError = validateEmail();
    if (validationError) {
      setResendError(validationError);
      return;
    }

    if (cooldown > 0 || isResending) {
      return;
    }

    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const response = await resendVerificationEmail(email.trim());
      const message =
        response?.detail?.message ||
        response?.message ||
        'Verification email sent. Please check your inbox.';

      setResendSuccess(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success(message);
      onSuccess?.(email.trim());
    } catch (error) {
      console.error('Resend verification email error:', error);
      const detail = error.response?.data?.detail;
      const message =
        (typeof detail === 'object' ? detail?.message : detail) ||
        error.response?.data?.message ||
        'Failed to resend verification email. Please try again.';
      setResendError(message);
    } finally {
      setIsResending(false);
    }
  };

  const canResend = cooldown === 0 && !isResending;
  const linkLabel = getLinkLabel({ isResending, cooldown });

  const renderResendLink = (prefix = "Didn't receive the email?") => (
    <p className="resend-verification-hint">
      {prefix}{' '}
      <button
        type="button"
        className="resend-verification-link"
        onClick={handleResend}
        disabled={!canResend}
      >
        {linkLabel}
      </button>
    </p>
  );

  const renderFeedback = () => (
    <>
      {resendError && (
        <p className="resend-verification-error">{resendError}</p>
      )}
      {resendSuccess && (
        <p className="resend-verification-success">
          Verification email sent. Check your inbox and spam folder.
        </p>
      )}
      {!resendSuccess && variant === 'link' && (
        <p className="resend-verification-note">
          Check your spam folder if you don&apos;t see the email within a few minutes.
        </p>
      )}
    </>
  );

  if (variant === 'link') {
    return (
      <div className={`resend-verification ${className}`}>
        {renderResendLink()}
        {renderFeedback()}
      </div>
    );
  }

  return (
    <div className={`resend-verification ${className}`}>
      {showEmailInput && !readOnlyEmail && (
        <div className="form-group">
          <label htmlFor="resend-verification-email" className="form-label">
            Email
          </label>
          <input
            type="email"
            id="resend-verification-email"
            className="form-input"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (resendError) setResendError('');
              if (resendSuccess) setResendSuccess(false);
            }}
            autoComplete="email"
            disabled={isResending}
          />
        </div>
      )}

      {readOnlyEmail && email && renderResendLink()}

      {!readOnlyEmail && renderResendLink('Need a new link?')}

      {renderFeedback()}
    </div>
  );
};

export default ResendVerificationEmail;
